import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { performInitialPoll } from './poller';
import { pollCycle } from './pollCycle';
import { subscribeToStatus as subToStatus } from './subscribers';
import { POLLING_TYPES } from './types';

// Polling interval in milliseconds (increased from 5000 to 10000 for performance)
const POLLING_INTERVAL = 10000;

/**
 * Custom hook that manages polling state and operations
 * @param {string} projectId Optional project ID from props
 * @returns {Object} Polling management operations and state
 */
export const usePollingManager = (propProjectId) => {
  const { id: routeProjectId } = useParams();
  const [pollingItems, setPollingItems] = useState(new Map());
  const [statuses, setStatuses] = useState(new Map());
  const pollingItemsRef = useRef(pollingItems);
  const statusesRef = useRef(statuses);
  const intervalIdRef = useRef(null);
  
  // Keep refs updated for use in interval callbacks
  useEffect(() => {
    pollingItemsRef.current = pollingItems;
  }, [pollingItems]);
  
  useEffect(() => {
    statusesRef.current = statuses;
  }, [statuses]);
  
  // Unregister item when done
  const unregisterPolling = useCallback((itemId) => {
    console.log(`[PollingContext] Unregistering ${itemId}`);
    setPollingItems(prev => {
      const newMap = new Map(prev);
      newMap.delete(itemId);
      return newMap;
    });
  }, []);
  
  // Register an item for polling
  const registerPolling = useCallback((itemId, itemType, initialData = {}) => {
    const projectId = propProjectId || initialData.projectId || routeProjectId;
    
    if (!projectId && itemType !== POLLING_TYPES.PROJECT) {
      console.warn(`[PollingContext] Missing projectId for ${itemType} item ${itemId}`);
    }
    
    // Check if already registered to prevent duplicate registrations
    if (pollingItemsRef.current.has(itemId)) {
      console.log(`[PollingContext] Item ${itemId} already registered, skipping`);
      return () => unregisterPolling(itemId);
    }
    
    console.log(`[PollingContext] Registering ${itemType} for ${itemId} in project ${projectId}`);
    
    // Add to polling items
    setPollingItems(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, { itemType, lastPolled: Date.now(), projectId });
      
      // Initialize status
      setStatuses(prevStatuses => {
        const newStatuses = new Map(prevStatuses);
        newStatuses.set(itemId, { 
          status: 'processing', 
          progress: initialData.progress || 0, 
          current_step: initialData.current_step || 'Starting...',
          data: initialData
        });
        return newStatuses;
      });
      
      return newMap;
    });
    
    // Immediate first poll
    setTimeout(() => {
      performInitialPoll(itemId, itemType, projectId, setStatuses, unregisterPolling);
    }, 500);
    
    return () => unregisterPolling(itemId);
  }, [propProjectId, routeProjectId, unregisterPolling]);
  
  // Get current status of an item
  const getItemStatus = useCallback((itemId) => {
    return statuses.get(itemId) || { status: null, progress: 0, current_step: null };
  }, [statuses]);
  
  // Check if an item is currently being polled
  const isPolling = useCallback((itemId) => pollingItems.has(itemId), [pollingItems]);
  
  // Setup polling interval
  useEffect(() => {
    // Clear any existing interval first
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    
    if (pollingItems.size === 0) return;
    
    console.log(`[PollingContext] Setting up polling for ${pollingItems.size} items`);
    
    // Run immediately first time
    pollCycle(pollingItemsRef.current, statusesRef.current, setStatuses, setPollingItems);
    
    // Poll at the specified interval
    intervalIdRef.current = setInterval(() => {
      pollCycle(pollingItemsRef.current, statusesRef.current, setStatuses, setPollingItems);
    }, POLLING_INTERVAL);

    return () => {
      if (intervalIdRef.current) {
        console.log('[PollingContext] Cleaning up polling interval');
        clearInterval(intervalIdRef.current);
        intervalIdRef.current = null;
      }
    };
  }, [pollingItems.size]); // Only depend on pollingItems.size
  
  return {
    registerPolling,
    unregisterPolling,
    getItemStatus,
    isPolling,
    subscribeToStatus: subToStatus,
    pollingItems: Array.from(pollingItems.entries()),
  };
}; 